import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import clsx from 'clsx'

import { Box, FormHelperText } from '@material-ui/core'

import { ScreenLoader } from './ScreenLoader'
import { StatusSyncTable } from './StatusSyncTable'
import { formatJobState, SyncProgressBar } from './SyncProgressBar'

import { useMessages } from '../hooks/snackbarHooks'
import { useApi, useLogic } from '../hooks/storeHook'
import { useBoolState, useLazyEffect } from '../hooks/commonHooks'
import { useSettingsPageStyles } from '../hooks/settingsPageStylesHook'

import { emDash } from '../common/stringUtility'
import { UnprocessedValueError } from '../common/tsUtils'
import { tryFormatDateTimeUnix } from '../common/dateTimeUtility'

import { ManualUserSyncJobModel } from '../server/mpsklad_core/Models/ManualUserSyncJobModel'
import { ManualUserSyncJobState } from '../server/mpsklad_core/Entity/Enums/ManualUserSyncJobState'

export const StatusScreen =
  observer(() => {
    const logic = useLogic()
    const {userSync} = useApi()
    const {showError} = useMessages()

    const classes = useSettingsPageStyles()

    // Block UI until jobs are loaded
    const [isLoading, setLoading, setLoaded] = useBoolState(true)
    const [isSubmitting, setSubmitting, setSubmitted] = useBoolState()
    const [activeJobId, setActiveJobId] = useState<string | null>(null)
    const [activeJob, setActiveJob] = useState<ManualUserSyncJobModel | null>(null)

    const [lastFinishedJob, setLastFinishedJob] = useState<ManualUserSyncJobModel | null>(null)

    const isPolling = !!activeJobId

    useEffect(() => {
      // Auto reset
      if (!activeJobId) {
        setActiveJob(null)
      }
    }, [activeJobId])

    useLazyEffect(async () => {
      setLoading()

      try {
        await logic.loadUserAccounts()

        // Load job statuses on mount
        const newActiveJob = await userSync.getActiveSyncJob()

        if (newActiveJob) {
          setActiveJob(newActiveJob)
          setActiveJobId(newActiveJob.id)
        } else {
          setActiveJobId(null)
        }

        setLastFinishedJob(await userSync.getLastFinishedSyncJob())
      } finally {
        setLoaded()
      }
    })

    // Poll active job
    useEffect(() => {
      if (!activeJobId) {
        return
      }

      const pollInterval = setInterval(async () => {
        const newActiveJob = await userSync.getSyncJob(activeJobId)

        switch (newActiveJob.state) {
          case ManualUserSyncJobState.Enqueued:
          case ManualUserSyncJobState.Processing:
            setActiveJob(newActiveJob)
            return

          case ManualUserSyncJobState.Succeeded:
          case ManualUserSyncJobState.Failed:
          case ManualUserSyncJobState.Skipped: {
            setActiveJobId(null)
            setLastFinishedJob(newActiveJob)
            clearInterval(pollInterval)
            return
          }

          default:
            throw new UnprocessedValueError('ManualUserSyncJobState', newActiveJob.state)
        }
      }, 5000)

      return () => clearInterval(pollInterval)
    }, [activeJobId, userSync])

    const onRefreshClick = async () => {
      if (isSubmitting || isPolling) {
        return
      }

      setSubmitting()

      try {
        const createdJob = await userSync.syncCurrentUser()

        setActiveJob(createdJob)
        setActiveJobId(createdJob.id)
      } catch (e) {
        console.log('Failed to sync current user', e)

        setActiveJob(null)
        setActiveJobId(null)
        showError('Не удалось запустить обновление!')
      } finally {
        setSubmitted()
      }
    }

    if (isLoading) {
      return <ScreenLoader/>
    }

    return (
      <Box display="flex" flexDirection="column" alignItems="flex-start" marginBottom={2}>
        <Box>
          <StatusSyncTable/>
        </Box>

        <FormHelperText>
          {isPolling ? 'Предыдущее' : 'Последнее'} ручное обновление: {
          !!lastFinishedJob
          ? `${formatJobState(lastFinishedJob)}, ${tryFormatDateTimeUnix(lastFinishedJob.finishedAtUnix) ?? emDash}`
          : 'нет данных'}.
        </FormHelperText>

        <Box width="100%" maxWidth={400}>
          <Box display="flex" marginLeft={1}>
            <button
              className={clsx(classes.saveButton, 'default-button')}
              disabled={isPolling}
              onClick={onRefreshClick}
            >
              <p>{isPolling ? 'Обновляем...' : 'Обновить'}</p>
            </button>
          </Box>

          {
            !!activeJob &&
            <SyncProgressBar syncJob={activeJob}/>
          }
        </Box>
      </Box>
    )
  })